import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { useTranslation } from 'react-i18next';
import { isValidLinkUrl, sanitizeUrl } from './url-validation';

interface Measurable {
  getBoundingClientRect(): DOMRect;
}

interface LinkEditPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (url: string, linkText?: string) => void;
  onRemove: () => void;
  initialUrl: string;
  isEditing: boolean;
  showLinkTextField?: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
}

export function LinkEditPopover({
  open,
  onOpenChange,
  onSubmit,
  onRemove,
  initialUrl,
  isEditing,
  showLinkTextField,
  anchorRef,
}: LinkEditPopoverProps) {
  const { t } = useTranslation('editor');
  const [url, setUrl] = useState(initialUrl);
  const [linkText, setLinkText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync initial URL when popover opens
  useEffect(() => {
    if (open) {
      setUrl(initialUrl);
      setLinkText('');
      // Focus the appropriate input after Radix renders content
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [open, initialUrl]);

  const isValid = url.trim() === '' ? false : isValidLinkUrl(url);

  const handleSubmit = useCallback(() => {
    if (!isValid) return;
    const trimmedLinkText = linkText.trim();
    onSubmit(sanitizeUrl(url), trimmedLinkText || undefined);
  }, [url, linkText, isValid, onSubmit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        onOpenChange(false);
      }
    },
    [handleSubmit, onOpenChange],
  );

  const virtualRef = useMemo(
    () => ({
      current: {
        getBoundingClientRect: () =>
          anchorRef.current?.getBoundingClientRect() ?? new DOMRect(),
      } as Measurable,
    }),
    [anchorRef],
  );

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Anchor virtualRef={virtualRef} />
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          className="z-50 rounded-md border bg-popover p-3 shadow-md"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
          }}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
          }}
          onInteractOutside={() => {
            onOpenChange(false);
          }}
        >
          <div className="flex flex-col gap-2">
            <input
              ref={showLinkTextField ? undefined : inputRef}
              type="text"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              placeholder={t('linkUrlPlaceholder')}
              className="h-8 w-64 rounded border bg-background px-2 text-sm outline-none focus:ring-1 focus:ring-ring"
            />
            {showLinkTextField && (
              <input
                ref={inputRef}
                type="text"
                value={linkText}
                onChange={(e) => {
                  setLinkText(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                placeholder={t('linkTextPlaceholder')}
                className="h-8 w-64 rounded border bg-background px-2 text-sm outline-none focus:ring-1 focus:ring-ring"
              />
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!isValid}
                className="rounded bg-primary px-3 py-1 text-sm text-primary-foreground disabled:opacity-50"
              >
                {t('linkApply')}
              </button>
              {isEditing && (
                <button
                  type="button"
                  onClick={onRemove}
                  className="rounded px-3 py-1 text-sm text-destructive hover:bg-destructive/10"
                >
                  {t('linkRemove')}
                </button>
              )}
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
